import { useLayoutEffect, useState } from 'react';

/**
 * A hook for lazy mounting components that need to stay mounted during closing animations.
 * The component will only mount when opened, but will remain mounted during the closing
 * animation to preserve transitions.
 *
 * @param open - Whether the component should be open
 * @param unmountDelay - Delay in milliseconds before unmounting after closing. Defaults to `200`.
 * @returns Whether the component should be mounted
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const shouldMount = useLazyMount(isOpen);
 *
 * return (
 *   <>
 *     <button onClick={() => setIsOpen(true)}>Open</button>
 *     {shouldMount && (
 *       <Dialog open={isOpen} onOpenChange={setIsOpen}>
 *         Content
 *       </Dialog>
 *     )}
 *   </>
 * );
 * ```
 */
export const useLazyMount = (open: boolean, unmountDelay = 200): boolean => {
  const [shouldMount, setShouldMount] = useState(open);

  useLayoutEffect(() => {
    if (open) {
      setShouldMount(true);

      return;
    }

    // Unmount after animation completes
    const timeout = setTimeout(() => {
      setShouldMount(false);
    }, unmountDelay);

    return () => clearTimeout(timeout);
  }, [open, unmountDelay]);

  return shouldMount;
};
